import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { createClient } from '@connectrpc/connect'
import { AdminService } from '../gen/cortado/v1/admin_pb'
import { connectTransport } from '../lib/connect-transport'
import { authActions } from '../stores/auth.store'
import { toast } from 'sonner'
import { AdminShell } from '../components/layout/admin-shell'
import { INACTIVE_ACCOUNT_MESSAGE, isInactiveAccountError } from '../lib/inactive-account'

export const Route = createFileRoute('/_admin')({
  component: AdminLayout,
})

function AdminLayout() {
  const navigate = useNavigate()
  const { session, isLoading, isAdmin } = useAuth()
  const [verifying, setVerifying] = useState(false)

  useEffect(() => {
    if (!isLoading && !session) {
      navigate({ to: '/login' })
    }
  }, [session, isLoading, navigate])

  useEffect(() => {
    if (!session || isAdmin !== null || verifying) return

    setVerifying(true)
    const client = createClient(AdminService, connectTransport)
    client
      .verifyAdmin({})
      .then(() => {
        authActions.setAdminStatus(true)
      })
      .catch(async (error) => {
        authActions.setAdminStatus(false)
        await authActions.signOut()
        toast.error(
          isInactiveAccountError(error)
            ? INACTIVE_ACCOUNT_MESSAGE
            : 'Access denied. You are not authorized as an admin.',
        )
        navigate({ to: '/login' })
      })
      .finally(() => setVerifying(false))
  }, [session, isAdmin, verifying, navigate])

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  if (isLoading || verifying || isAdmin === null) {
    return (
      <div className="flex min-h-svh items-center justify-center px-4">
        <p className="text-muted-foreground">Verifying access...</p>
      </div>
    )
  }

  if (!session || !isAdmin) {
    return null
  }

  return (
    <AdminShell
      onSignOut={async () => {
        await authActions.signOut()
        navigate({ to: '/login' })
      }}
    >
      <Outlet />
    </AdminShell>
  )
}
